import React, { useMemo } from 'react';

import { HistoryLocation, NavigationHistoryPath } from '../../types';

import { Location } from './Location/Location';
import { LocationTitle } from './LocationTitle/LocationTitle';
import { Icon } from 'components';
import { Path } from './Path/Path';

import { useIntl } from 'react-intl';
import { useStyles } from './styles';

import { checkIsTrackedLocationHistoryPath } from '../../utils';
import { useClientEventsHistoryFilter, useGetHistoryWithClientEventOnRoute } from 'entities/navigation';
import { ClientTransactionEvent } from './ClientTransactionEvent/ClientTransactionEvent';

export const NavigationHistoryPathDescription = ({
  startLocation,
  endLocation,
  navigationHistoryPath = [],
}: NavigationHistoryPathDescriptionProps) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  const historyEvents = useGetHistoryWithClientEventOnRoute(navigationHistoryPath);

  const clientEventHistoryFilter = useClientEventsHistoryFilter();

  const filteredNavigationHistoryPath = useMemo(
    () =>
      historyEvents
        .filter((event) => {
          if (clientEventHistoryFilter.length === 0) {
            return true;
          }
          return clientEventHistoryFilter.includes(event.type);
        })
        .filter((event) => {
          if ('locations' in event) {
            return !checkIsTrackedLocationHistoryPath(event.type);
          }

          return true;
        }),
    [clientEventHistoryFilter, historyEvents]
  );

  return (
    <div>
      <br />
      <div className={classes.list}>
        {startLocation && (
          <>
            <LocationTitle padding={{ top: 2.5 }}>{formatMessage({ id: 'start' })}</LocationTitle>
            <Location
              startLocation={startLocation}
              icon={<Icon kind={'startLocation'} color="primary" />}
              warningMessage={
                startLocation.isLate
                  ? formatMessage({
                      id: 'navigationHistoryStartLocationWarningMessage',
                    })
                  : null
              }
            />
          </>
        )}
        {filteredNavigationHistoryPath.map((event) => {
          if ('locations' in event) {
            const { locations, type } = event;
            return <Path startLocation={locations[0]} endLocation={locations[locations.length - 1]} type={type} />;
          }
          return <ClientTransactionEvent historyEvent={event} />;
        })}
        {endLocation && (
          <>
            <LocationTitle>{formatMessage({ id: 'end' })}</LocationTitle>
            <Location
              startLocation={endLocation}
              icon={<Icon kind={'endLocation'} color="secondary" />}
              warningMessage={
                endLocation.isLate
                  ? formatMessage({
                      id: 'navigationHistoryEndLocationWarningMessage',
                    })
                  : null
              }
            />
          </>
        )}
      </div>
    </div>
  );
};

interface NavigationHistoryPathDescriptionProps {
  startLocation?: HistoryLocation | null;
  endLocation?: HistoryLocation | null;
  navigationHistoryPath?: NavigationHistoryPath[];
}
